/* ============================================================================
   rbk-prem.js — engine for the rp-* premium sections.
   ROLLBACK: delete this file + its <script> tag in layout/theme.liquid.
   ----------------------------------------------------------------------------
   Design rules enforced here:
   - transform/opacity only; reveals play once (content never re-hides on scroll-back)
   - prefers-reduced-motion: everything snaps to final state, no listeners
   - theme-editor safe: re-inits on shopify:section:load, cleans on unload
   - GSAP/ScrollTrigger are OPTIONAL polish: if present we refresh triggers after
     fonts load; the core engine is IntersectionObserver + rAF and works alone.
   NOTE: block comments only in this file (Shopify minification can glue a //
   comment onto following code and silently break it).
   ========================================================================== */
(function () {
  'use strict';
  /* gate for the hidden reveal states: without this class everything stays visible,
     so a JS failure can never ship a blank page */
  document.documentElement.classList.add('rp-js');
  /* REDUCE gates every scroll-linked animation below (parallax, manifesto/text-highlight,
     kinetic, stats, journey, tilt, the reveal transition). window.__rbkForceMotion is the
     merchant override ("Play motion even with reduced motion") set from theme settings: when
     on, we ignore the visitor's reduced-motion preference across ALL of these at once — a
     brand that treats motion as core, deciding deliberately. Default off, because this is an
     accessibility signal. Same shape as the Lenis and reels force overrides. */
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__rbkForceMotion;
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------- reveal engine ----------------
     IO is the primary trigger; `pending` + sweep() is the safety net. Chrome throttles IO
     callbacks in hidden tabs, so a page opened in a background tab could keep whole
     sections at opacity 0 (= the "page looks bugged / ended" white voids). The sweep
     force-reveals anything inside the viewport on scroll / tab-focus / bfcache restore. */
  var pending = [];
  function revealNow(el) { el.classList.add('is-in'); }
  function sweep() {
    if (!pending.length) return;
    var vh = window.innerHeight;
    for (var i = pending.length - 1; i >= 0; i--) {
      var el = pending[i];
      if (!el.isConnected) { pending.splice(i, 1); continue; }
      var r = el.getBoundingClientRect();
      if (r.top < vh * 0.96 && r.bottom > -40) { revealNow(el); pending.splice(i, 1); }
    }
  }
  function initReveals(root) {
    var els = (root || document).querySelectorAll('.rp-rev:not(.is-in)');
    if (!els.length) return;
    if (REDUCE || !('IntersectionObserver' in window)) {
      els.forEach(revealNow);
      return;
    }
    var vh = window.innerHeight;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          revealNow(en.target); io.unobserve(en.target);
          var ix = pending.indexOf(en.target);
          if (ix > -1) pending.splice(ix, 1);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.04 });
    els.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh * 0.92 && r.bottom > 0) {
        /* already on screen at boot: reveal next tick so the entrance still animates
           but never depends on an IO callback */
        setTimeout(function () { revealNow(el); }, 30);
      } else {
        pending.push(el);
        io.observe(el);
      }
    });
    if (!initReveals._bound) {
      initReveals._bound = true;
      var raf = null;
      function onMove() { if (!raf) raf = requestAnimationFrame(function () { raf = null; sweep(); }); }
      window.addEventListener('scroll', onMove, { passive: true });
      window.addEventListener('resize', onMove);
      document.addEventListener('visibilitychange', function () { if (!document.hidden) sweep(); });
      window.addEventListener('pageshow', sweep);
      window.addEventListener('load', function () { setTimeout(sweep, 350); });
    }
  }

  /* stagger: parent[data-rp-stagger] assigns rising --rp-d to .rp-rev children
     (capped: a long stagger chain must never hold media hostage for seconds) */
  function initStagger(root) {
    (root || document).querySelectorAll('[data-rp-stagger]').forEach(function (p) {
      var step = parseFloat(p.getAttribute('data-rp-stagger')) || 0.08;
      p.querySelectorAll('.rp-rev').forEach(function (el, i) {
        el.style.setProperty('--rp-d', Math.min(i * step, 0.32).toFixed(2) + 's');
      });
    });
  }

  /* ---------------- parallax (max +/-8% per brief) ---------------- */
  var parEls = [];
  var parRaf = null;
  function parTick() {
    parRaf = null;
    var vh = window.innerHeight;
    for (var i = 0; i < parEls.length; i++) {
      var el = parEls[i];
      var r = el.getBoundingClientRect();
      if (r.bottom < -80 || r.top > vh + 80) continue;
      var f = parseFloat(el.getAttribute('data-rp-par')) || 0.06;
      if (f > 0.08) f = 0.08;
      var prog = (r.top + r.height / 2 - vh / 2) / (vh / 2);
      el.style.transform = 'translateY(' + (-prog * f * 100).toFixed(2) + '%)';
    }
  }
  function onScrollPar() { if (!parRaf) parRaf = requestAnimationFrame(parTick); }
  function initParallax(root) {
    /* R17 perf: parallax is desktop-only now. On touch it was scroll-jank +
       wasted main-thread scroll handlers on the exact devices Lighthouse
       throttles hardest (mobile). FINE = hover + fine pointer. */
    if (REDUCE || !FINE) return;
    (root || document).querySelectorAll('[data-rp-par]').forEach(function (el) {
      if (parEls.indexOf(el) === -1) parEls.push(el);
    });
    if (parEls.length && !initParallax._bound) {
      initParallax._bound = true;
      window.addEventListener('scroll', onScrollPar, { passive: true });
      window.addEventListener('resize', onScrollPar);
      onScrollPar();
    }
  }

  /* ---------------- manifesto word scrub ---------------- */
  function initManifesto(root) {
    (root || document).querySelectorAll('[data-rp-manifesto]:not([data-rp-ready])').forEach(function (box) {
      box.setAttribute('data-rp-ready', '1');
      var p = box.querySelector('p');
      if (!p) return;
      var words = p.textContent.trim().split(/\s+/);
      p.innerHTML = words.map(function (w) { return '<span class="rp-w">' + w + '</span>'; }).join(' ');
      var spans = p.querySelectorAll('.rp-w');
      if (REDUCE) { spans.forEach(function (s) { s.classList.add('is-on'); }); return; }
      var raf = null;
      function update() {
        raf = null;
        var r = box.getBoundingClientRect();
        var vh = window.innerHeight;
        var prog = (vh * 0.85 - r.top) / (r.height + vh * 0.45);
        prog = Math.max(0, Math.min(1, prog));
        var n = Math.round(prog * spans.length);
        for (var i = 0; i < spans.length; i++) spans[i].classList.toggle('is-on', i < n);
      }
      window.addEventListener('scroll', function () { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
      update();
    });
  }

  /* ---------------- stats count-up ---------------- */
  function initStats(root) {
    var nums = (root || document).querySelectorAll('[data-rp-count]:not([data-rp-ready])');
    if (!nums.length) return;
    nums.forEach(function (el) {
      el.setAttribute('data-rp-ready', '1');
      var target = parseFloat(el.getAttribute('data-rp-count')) || 0;
      if (REDUCE || !('IntersectionObserver' in window)) { el.textContent = el.getAttribute('data-rp-final'); return; }
      var io = new IntersectionObserver(function (en) {
        if (!en[0].isIntersecting) return;
        io.disconnect();
        var t0 = null, dur = 1400;
        function step(t) {
          if (!t0) t0 = t;
          var p = Math.min(1, (t - t0) / dur);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased).toLocaleString('en-IN');
          if (p < 1) requestAnimationFrame(step);
          else el.textContent = el.getAttribute('data-rp-final');
        }
        requestAnimationFrame(step);
      }, { threshold: 0.5 });
      io.observe(el);
    });
  }

  /* per-strip scroll-progress pill (its OWN indicator, not the page scrollbar).
     Inserted as a sibling after the strip; reflects scrollLeft, hidden when the
     strip doesn't overflow. The thumb can be dragged to scrub. */
  function initStripBar(strip) {
    if (strip.__rpBar || strip.getAttribute('data-rp-nobar') !== null) return;
    var bar = document.createElement('div');
    bar.className = 'rp-stripbar';
    bar.innerHTML = '<span class="rp-stripbar__thumb"></span>';
    strip.insertAdjacentElement('afterend', bar);
    var thumb = bar.querySelector('.rp-stripbar__thumb');
    strip.__rpBar = bar;
    function sync() {
      var max = strip.scrollWidth - strip.clientWidth;
      if (max <= 2) { bar.classList.remove('is-on'); return; }
      bar.classList.add('is-on');
      var ratio = strip.clientWidth / strip.scrollWidth;
      var p = strip.scrollLeft / max;
      thumb.style.width = (ratio * 100).toFixed(2) + '%';
      thumb.style.transform = 'translateX(' + (p * (100 / Math.max(0.0001, ratio) - 100)).toFixed(2) + '%)';
    }
    function syncSoon() { if (strip.__rpBarRaf) return; strip.__rpBarRaf = requestAnimationFrame(function () { strip.__rpBarRaf = null; sync(); }); }
    strip.addEventListener('scroll', syncSoon, { passive: true });
    window.addEventListener('resize', sync);
    /* THE "pill never loads until hard refresh" fix: sync() ran before the lazy
       cover images had width (scrollWidth==clientWidth -> bar hidden) and never
       re-ran. Now we re-sync whenever the content size actually settles:
       - every cover image's load event (scrollWidth grows as each one paints)
       - a ResizeObserver on the strip + its cards (catches lazy reflow + layout)
       - window load + a few delayed ticks as a belt-and-suspenders backstop. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);
    window.addEventListener('load', sync);
    [120, 450, 1100, 2200].forEach(function (t) { setTimeout(sync, t); });
    strip.querySelectorAll('img').forEach(function (img) { if (!img.complete) img.addEventListener('load', sync, { passive: true }); });
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(syncSoon);
      ro.observe(strip);
      Array.prototype.forEach.call(strip.children, function (c) { try { ro.observe(c); } catch (e) {} });
    }
    sync();
    /* drag the thumb / click the track to scrub — ALL pointers (R10: the FINE
       gate kept phones from grabbing the pill; touch-action:none in CSS stops
       the page from scrolling while scrubbing) */
    var bdown = false;
    function scrub(clientX) {
      var r = bar.getBoundingClientRect();
      var p = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      strip.scrollLeft = p * (strip.scrollWidth - strip.clientWidth);
    }
    bar.addEventListener('pointerdown', function (e) { bdown = true; bar.setPointerCapture(e.pointerId); scrub(e.clientX); e.preventDefault(); });
    bar.addEventListener('pointermove', function (e) { if (bdown) scrub(e.clientX); });
    bar.addEventListener('pointerup', function () { bdown = false; });
    bar.addEventListener('pointercancel', function () { bdown = false; });
  }

  /* world-index marquee: a scrub pill under the auto-loop. Hold it to pause
     the loop and seek (desktop pointer AND touch); release and the loop
     resumes from exactly where you left it. Seeking uses the Web Animations
     API handle of the CSS animation (currentTime); pause stays CSS-owned via
     .is-scrub so the existing hover-pause keeps working after release. */
  /* Shared chrome for both marquee modes: [<] [scrub bar] [>] inserted after the
     viewport. Hoisted out of initMarqueeScrub so the static fallback below builds an
     identical control strip instead of a second, divergent one. */
  function mkArrow(label, d) {
    var b = document.createElement('button');
    b.type = 'button'; b.className = 'rp-mq-arrow'; b.setAttribute('aria-label', label);
    b.innerHTML = '<svg viewBox="0 0 9 16" fill="none" aria-hidden="true"><path d="' + d + '" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return b;
  }
  function buildMarqueeCtl(viewport) {
    var bar = document.createElement('div');
    bar.className = 'rp-stripbar rp-stripbar--mq is-on';
    bar.innerHTML = '<span class="rp-stripbar__thumb"></span>';
    var ctl = document.createElement('div');
    ctl.className = 'rp-mq-ctl';
    var prevBtn = mkArrow('Previous world', 'M8 1 1 8l7 7');
    var nextBtn = mkArrow('Next world', 'M1 1l7 7-7 7');
    ctl.appendChild(prevBtn); ctl.appendChild(bar); ctl.appendChild(nextBtn);
    viewport.insertAdjacentElement('afterend', ctl);
    return { bar: bar, thumb: bar.querySelector('.rp-stripbar__thumb'), prev: prevBtn, next: nextBtn };
  }

  /* STATIC FALLBACK — no CSS animation to drive (prefers-reduced-motion sets
     `animation: none`, and some engines expose no Web Animations handle).

     This used to be a dead end: every control — arrows, scrub bar, drag — was built
     FROM the animation object, so killing the animation removed all of them while the
     viewport stayed `overflow: hidden`. On a 1270px viewport with a 3392px track that
     sealed 2122px of worlds off with no way to reach them.

     Reduced motion means "stop moving things on your own", not "take away the
     navigation". So: turn the viewport into a real horizontal scroller and wire the
     same arrows, bar and drag to scrollLeft. The content stays reachable; nothing
     moves unless the shopper moves it. */
  function initMarqueeStatic(track, viewport) {
    viewport.classList.add('is-static', 'rp-marquee__viewport--grab');
    var c = buildMarqueeCtl(viewport);

    function maxScroll() { return Math.max(1, track.scrollWidth - viewport.clientWidth); }
    function cardStep() {
      var card = track.querySelector('.rp-wx__card');
      return card ? card.getBoundingClientRect().width + 24 : viewport.clientWidth * 0.6;
    }
    function paint() {
      var r = Math.min(0.9, Math.max(0.08, viewport.clientWidth / Math.max(1, track.scrollWidth)));
      var p = viewport.scrollLeft / maxScroll();
      c.thumb.style.width = (r * 100).toFixed(2) + '%';
      c.thumb.style.transform = 'translateX(' + (p * (100 / r - 100)).toFixed(2) + '%)';
    }
    viewport.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint);
    paint();

    c.prev.addEventListener('click', function () { viewport.scrollBy({ left: -cardStep(), behavior: 'smooth' }); });
    c.next.addEventListener('click', function () { viewport.scrollBy({ left: cardStep(), behavior: 'smooth' }); });

    /* Drag the bar to seek. */
    var held = false;
    function seek(clientX) {
      var r = c.bar.getBoundingClientRect();
      var p = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      viewport.scrollLeft = p * maxScroll();
    }
    c.bar.addEventListener('pointerdown', function (e) {
      held = true; c.bar.setPointerCapture(e.pointerId); seek(e.clientX); e.preventDefault();
    });
    c.bar.addEventListener('pointermove', function (e) { if (held) seek(e.clientX); });
    c.bar.addEventListener('pointerup', function () { held = false; });
    c.bar.addEventListener('pointercancel', function () { held = false; });

    /* Drag the rail itself. Touch already scrolls natively (overflow-x:auto), so this
       is for mouse; the 4px threshold keeps a genuine click on a world card working. */
    var vHeld = false, vX = 0, vLeft = 0, vMoved = false;
    viewport.addEventListener('pointerdown', function (e) {
      if (e.button > 0 || e.pointerType === 'touch') return;
      vHeld = true; vMoved = false; vX = e.clientX; vLeft = viewport.scrollLeft;
      viewport.classList.add('is-drag');
      try { viewport.setPointerCapture(e.pointerId); } catch (x) {}
    });
    viewport.addEventListener('pointermove', function (e) {
      if (!vHeld) return;
      var dx = e.clientX - vX;
      if (Math.abs(dx) > 4) vMoved = true;
      viewport.scrollLeft = vLeft - dx;
    });
    function vUp(e) {
      if (!vHeld) return;
      vHeld = false; viewport.classList.remove('is-drag');
      try { viewport.releasePointerCapture(e.pointerId); } catch (x) {}
    }
    viewport.addEventListener('pointerup', vUp);
    viewport.addEventListener('pointercancel', vUp);
    viewport.addEventListener('click', function (e) { if (vMoved) { e.preventDefault(); e.stopPropagation(); } }, true);
  }

  function initMarqueeScrub(root) {
    var pending = false;
    (root || document).querySelectorAll('.rp-wx--marquee .rp-marquee__track:not([data-rp-scrub])').forEach(function (track) {
      var viewport = track.closest('.rp-marquee__viewport') || track.parentElement;

      /* Is the CSS auto-loop actually running? Read the computed animation-name rather
         than waiting on getAnimations(): under prefers-reduced-motion the stylesheet
         sets `animation: none`, and that is knowable immediately instead of after 40
         frames of retries that can only ever fail. */
      /* __rbkForceMotion guarantees the CSS auto-loop is restored (the override class in
         rbk-prem.css re-enables `animation: rp-marq`). But that stylesheet can apply a beat
         AFTER this critical-boot init reads animationName, so a bare read can still see
         'none' and wrongly commit to static — leaving the CSS loop AND the static scroller
         both running. So: when force-motion is on, never take the static path; fall through
         to the animated path, which already retries until getAnimations() is ready. */
      if (getComputedStyle(track).animationName === 'none' && !window.__rbkForceMotion) {
        track.setAttribute('data-rp-scrub', 'static');
        /* Claim data-rp-ready so the idle initMarquee does NOT duplicate the content:
           in a static scroller a duplicated track means scrolling past the same worlds
           twice. Duplication only makes sense for the seamless loop. */
        track.setAttribute('data-rp-ready', '1');
        initMarqueeStatic(track, viewport);
        return;
      }

      /* The seamless loop needs the content TWICE — every measurement below divides
         scrollWidth by 2. initMarquee does this duplication too, but it runs in the
         IDLE boot phase, up to 1.8s after this one, so the thumb ratio and the arrow
         step would be computed against a half-length track. Do it here, first.
         Idempotent via data-rp-ready, so initMarquee then skips it. */
      if (!track.hasAttribute('data-rp-ready')) {
        track.setAttribute('data-rp-ready', '1');
        track.innerHTML += track.innerHTML;
      }

      if (!track.getAnimations) return;
      var anims = track.getAnimations();
      var anim = anims[0], durMs = 0;
      if (anim) { try { durMs = anim.effect.getComputedTiming().duration; } catch (e) {} }
      /* The CSS marquee animation can register a frame or two AFTER this critical-boot
         init runs. The old code set data-rp-scrub then returned early, permanently
         skipping the pill + drag. Now: don't mark until ready, and retry next frame. */
      if (!anim || !durMs) { pending = true; return; }
      track.setAttribute('data-rp-scrub', '1');
      var c = buildMarqueeCtl(viewport);
      var bar = c.bar, prevBtn = c.prev, nextBtn = c.next;
      var thumb = bar.querySelector('.rp-stripbar__thumb');
      /* thumb size = share of one loop copy visible in the viewport */
      function ratio() {
        var half = track.scrollWidth / 2;
        return Math.min(0.9, Math.max(0.08, half > 0 ? viewport.clientWidth / half : 0.2));
      }
      var lastP = -1;
      (function paint() {
        requestAnimationFrame(paint);
        var p = ((anim.currentTime || 0) % durMs) / durMs;
        if (Math.abs(p - lastP) < 0.0005) return; /* paused: skip the write */
        lastP = p;
        var r = ratio();
        thumb.style.width = (r * 100).toFixed(2) + '%';
        thumb.style.transform = 'translateX(' + (p * (100 / r - 100)).toFixed(2) + '%)';
      })();
      var held = false;
      function seek(clientX) {
        var r = bar.getBoundingClientRect();
        var p = Math.min(0.999, Math.max(0, (clientX - r.left) / r.width));
        try { anim.currentTime = p * durMs; } catch (e) {}
      }
      bar.addEventListener('pointerdown', function (e) {
        held = true; track.classList.add('is-scrub');
        bar.setPointerCapture(e.pointerId); seek(e.clientX); e.preventDefault();
      });
      bar.addEventListener('pointermove', function (e) { if (held) seek(e.clientX); });
      function release() { held = false; track.classList.remove('is-scrub'); }
      bar.addEventListener('pointerup', release);
      bar.addEventListener('pointercancel', release);

      /* R22: arrows nudge the auto-loop by ~one card (wraps the animation time) */
      function stepAnim(dir) {
        var card = track.querySelector('.rp-wx__card');
        var half = track.scrollWidth / 2;
        var cw = card ? (card.getBoundingClientRect().width + 24) : half * 0.18;
        var frac = half > 0 ? cw / half : 0.15;
        var t = (anim.currentTime || 0) + dir * frac * durMs;
        t = ((t % durMs) + durMs) % durMs;
        try { anim.currentTime = t; } catch (e) {}
      }
      prevBtn.addEventListener('click', function () { stepAnim(-1); });
      nextBtn.addEventListener('click', function () { stepAnim(1); });

      /* R24: hold-and-swipe directly on the marquee (mobile + desktop). Holding pauses
         the auto-loop; dragging scrubs it via the SAME Web-Animations seek the pill
         uses; releasing resumes from there. touch-action:pan-y (set in CSS) keeps
         vertical page scrolling working while we own the horizontal drag. */
      viewport.classList.add('rp-marquee__viewport--grab');
      var vHeld = false, vStartX = 0, vStartT = 0, vMoved = false;
      function pxToTime() { var half = track.scrollWidth / 2; return half > 0 ? (durMs / half) : 0; }
      viewport.addEventListener('pointerdown', function (e) {
        if (e.button > 0) return;
        vHeld = true; vMoved = false; vStartX = e.clientX; vStartT = (anim.currentTime || 0);
        track.classList.add('is-scrub'); viewport.classList.add('is-drag');
        try { viewport.setPointerCapture(e.pointerId); } catch (x) {}
      });
      viewport.addEventListener('pointermove', function (e) {
        if (!vHeld) return;
        var dx = e.clientX - vStartX;
        if (Math.abs(dx) > 4) vMoved = true;
        var t = vStartT - dx * pxToTime();        /* follow the finger (track scrolls left as time grows) */
        t = ((t % durMs) + durMs) % durMs;
        try { anim.currentTime = t; } catch (e2) {}
      });
      function vUp(e) {
        if (!vHeld) return;
        vHeld = false; viewport.classList.remove('is-drag');
        try { viewport.releasePointerCapture(e.pointerId); } catch (x) {}
        track.classList.remove('is-scrub');        /* resume the auto-loop from here */
      }
      viewport.addEventListener('pointerup', vUp);
      viewport.addEventListener('pointercancel', vUp);
      /* a real drag must not fire the card's link navigation (a clean tap still does) */
      viewport.addEventListener('click', function (e) { if (vMoved) { e.preventDefault(); e.stopPropagation(); } }, true);
    });
    /* an animation wasn't ready yet — try again next frame (capped, so a genuinely
       animation-less marquee under reduced-motion doesn't loop forever). */
    if (pending && (initMarqueeScrub._t = (initMarqueeScrub._t || 0) + 1) < 40) requestAnimationFrame(function () { initMarqueeScrub(root); });
  }

  /* ---------------- gallery drag (desktop pointer drag; native touch scroll) ---------------- */
  function initGallery(root) {
    (root || document).querySelectorAll('.rp-gallery__strip:not([data-rp-ready])').forEach(function (strip) {
      strip.setAttribute('data-rp-ready', '1');
      /* R23: swipe-only mode (data-rp-no-hscroll, set by the RP World "Drag strip"
         layout setting) — keep the scrub pill + native touch swipe, but let Lenis
         own the wheel (no desktop wheel-hijack / drag) so the page scrolls normally. */
      var noH = strip.hasAttribute('data-rp-no-hscroll');
      /* Lenis owns the page wheel; without this attribute it eats wheel events
         over inner scrollers, so strips felt dead on desktop */
      if (!noH) strip.setAttribute('data-lenis-prevent', '');
      initStripBar(strip);
      if (noH || !FINE) return;
      /* ---- smooth eased scroll (GSAP-like) ----
         A target position is lerped toward each frame, so wheel scrolling glides
         instead of stepping, and a drag-release carries momentum that decays. */
      var target = strip.scrollLeft, raf = null, vel = 0;
      function maxScroll() { return strip.scrollWidth - strip.clientWidth; }
      function clampT() { target = Math.max(0, Math.min(target, maxScroll())); }
      function loop() {
        var cur = strip.scrollLeft;
        var next = cur + (target - cur) * 0.14;
        if (Math.abs(target - next) < 0.4) { next = target; raf = null; }
        strip.scrollLeft = next;
        if (raf !== null) raf = requestAnimationFrame(loop);
      }
      function run() { if (raf === null) { raf = requestAnimationFrame(loop); } }
      strip.addEventListener('wheel', function (e) {
        if (maxScroll() <= 4) return;
        var d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        var atStart = target <= 0 && d < 0;
        var atEnd = target >= maxScroll() - 1 && d > 0;
        if (atStart || atEnd) return;
        e.preventDefault();
        target += d * 1.1; clampT(); run();
      }, { passive: false });

      var down = false, startX = 0, startT = 0, moved = false, lastX = 0, lastDX = 0;
      strip.addEventListener('pointerdown', function (e) {
        down = true; moved = false; startX = e.clientX; startT = target = strip.scrollLeft;
        lastX = e.clientX; lastDX = 0; if (raf !== null) { cancelAnimationFrame(raf); raf = null; }
        strip.classList.add('is-drag'); strip.setPointerCapture(e.pointerId);
      });
      strip.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        lastDX = e.clientX - lastX; lastX = e.clientX;
        target = startT - dx; clampT();
        strip.scrollLeft = target; /* 1:1 while dragging */
      });
      function up(e) {
        if (!down) return;
        down = false; strip.classList.remove('is-drag');
        try { strip.releasePointerCapture(e.pointerId); } catch (x) {}
        /* fling: carry the last drag velocity into eased momentum */
        if (moved && Math.abs(lastDX) > 2) { target = strip.scrollLeft - lastDX * 12; clampT(); run(); }
      }
      strip.addEventListener('pointerup', up);
      strip.addEventListener('pointercancel', up);
      strip.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    });
  }

  /* ---------------- marquee: duplicate track content for a seamless loop ---------------- */
  function initMarquee(root) {
    (root || document).querySelectorAll('.rp-marquee__track:not([data-rp-ready])').forEach(function (track) {
      track.setAttribute('data-rp-ready', '1');
      track.innerHTML += track.innerHTML;
    });
  }

  /* ---------------- FAQ accordion ---------------- */
  function initFaq(root) {
    (root || document).querySelectorAll('.rp-faq__list:not([data-rp-ready])').forEach(function (list) {
      list.setAttribute('data-rp-ready', '1');
      list.addEventListener('click', function (e) {
        var q = e.target.closest('.rp-faq__q');
        if (!q) return;
        var item = q.closest('.rp-faq__item');
        var open = item.classList.contains('is-open');
        list.querySelectorAll('.rp-faq__item.is-open').forEach(function (o) {
          o.classList.remove('is-open');
          o.querySelector('.rp-faq__q').setAttribute('aria-expanded', 'false');
        });
        if (!open) { item.classList.add('is-open'); q.setAttribute('aria-expanded', 'true'); }
      });
    });
  }

  /* ---------------- premium FAQ page: search + category scroll-spy ---------------- */
  function initFaqPage(root) {
    (root || document).querySelectorAll('[data-rp-faqpage]:not([data-rp-ready])').forEach(function (sec) {
      sec.setAttribute('data-rp-ready', '1');
      var input = sec.querySelector('[data-rp-faq-search]');
      var items = Array.prototype.slice.call(sec.querySelectorAll('[data-rp-faq-q]'));
      var groups = Array.prototype.slice.call(sec.querySelectorAll('[data-rp-faq-group]'));
      var empty = sec.querySelector('[data-rp-faq-empty]');
      if (input) {
        input.addEventListener('input', function () {
          var q = input.value.toLowerCase().trim();
          var any = false;
          items.forEach(function (it) {
            var hay = (it.getAttribute('data-rp-faq-q') || '') + ' ' + it.textContent.toLowerCase();
            var hit = !q || hay.indexOf(q) > -1;
            it.style.display = hit ? '' : 'none';
            if (hit) any = true;
          });
          groups.forEach(function (g) {
            var vis = g.querySelectorAll('[data-rp-faq-q]:not([style*="display: none"])');
            g.style.display = vis.length ? '' : 'none';
          });
          if (empty) empty.hidden = any;
        });
      }
      /* scroll-spy the category rail */
      var links = Array.prototype.slice.call(sec.querySelectorAll('[data-rp-faq-cat]'));
      if (links.length && groups.length && 'IntersectionObserver' in window) {
        var spy = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            var id = en.target.getAttribute('data-rp-faq-group');
            links.forEach(function (l) { l.classList.toggle('is-active', l.getAttribute('data-rp-faq-cat') === id); });
          });
        }, { rootMargin: '-30% 0px -60% 0px', threshold: 0.01 });
        groups.forEach(function (g) { spy.observe(g); });
      }
    });
  }

  /* ---------------- legal page: clause index scroll-spy ---------------- */
  function initLegal(root) {
    (root || document).querySelectorAll('[data-rp-legal]:not([data-rp-ready])').forEach(function (sec) {
      sec.setAttribute('data-rp-ready', '1');
      var links = Array.prototype.slice.call(sec.querySelectorAll('[data-rp-legal-link]'));
      var clauses = Array.prototype.slice.call(sec.querySelectorAll('[data-rp-legal-clause]'));
      if (!links.length || !clauses.length || !('IntersectionObserver' in window)) return;
      var spy = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var id = en.target.getAttribute('data-rp-legal-clause');
          links.forEach(function (l) { l.classList.toggle('is-active', l.getAttribute('data-rp-legal-link') === id); });
        });
      }, { rootMargin: '-20% 0px -70% 0px', threshold: 0.01 });
      clauses.forEach(function (c) { spy.observe(c); });
    });
  }

  /* ---------------- video click-to-play ---------------- */
  function initVideo(root) {
    (root || document).querySelectorAll('.rp-video:not([data-rp-ready])').forEach(function (box) {
      box.setAttribute('data-rp-ready', '1');
      var btn = box.querySelector('.rp-video__play');
      var vid = box.querySelector('video');
      if (!btn || !vid) return;
      btn.addEventListener('click', function () {
        box.classList.add('is-playing');
        vid.setAttribute('controls', 'controls');
        vid.play().catch(function () {});
      });
    });
  }

  /* ---------------- shop-the-look hotspots ----------------
     Delegated: pins toggle their mini card, opening one closes its siblings,
     any click outside a pin closes everything. Works the same for touch. */
  function initHotspot() {
    if (initHotspot._bound) return;
    initHotspot._bound = true;
    document.addEventListener('click', function (e) {
      var dot = e.target.closest && e.target.closest('.rp-hs__dot');
      if (dot) {
        var pin = dot.closest('.rp-hs__pin');
        var stage = dot.closest('[data-rp-hotspot]');
        var open = pin.classList.contains('is-open');
        stage.querySelectorAll('.rp-hs__pin.is-open').forEach(function (p) {
          p.classList.remove('is-open');
          p.querySelector('.rp-hs__dot').setAttribute('aria-expanded', 'false');
        });
        if (!open) {
          pin.classList.add('is-open');
          dot.setAttribute('aria-expanded', 'true');
          var card = pin.querySelector('.rp-hs__card');
          if (card) card.setAttribute('tabindex', '0');
        }
        return;
      }
      if (!e.target.closest || !e.target.closest('.rp-hs__pin')) {
        document.querySelectorAll('.rp-hs__pin.is-open').forEach(function (p) {
          p.classList.remove('is-open');
          p.querySelector('.rp-hs__dot').setAttribute('aria-expanded', 'false');
        });
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('.rp-hs__pin.is-open').forEach(function (p) {
        p.classList.remove('is-open');
        p.querySelector('.rp-hs__dot').setAttribute('aria-expanded', 'false');
      });
    });
  }

  /* ---------------- hover accordion: tap support ----------------
     Fine pointers expand on hover via CSS. On touch, the first tap activates a
     slice (suppressing its link), the second tap follows the link. */
  function initAccordion() {
    if (initAccordion._bound) return;
    initAccordion._bound = true;
    document.addEventListener('click', function (e) {
      var slice = e.target.closest && e.target.closest('[data-rp-acc] .rp-acc__slice');
      if (!slice) return;
      if (FINE) return;
      if (slice.classList.contains('is-act')) return;
      e.preventDefault();
      slice.parentElement.querySelectorAll('.rp-acc__slice.is-act').forEach(function (s) { s.classList.remove('is-act'); });
      slice.classList.add('is-act');
    });
  }

  /* ---------------- collection card 3D tilt ----------------
     Gated to fine pointers with hover and no reduced-motion (the brief's rule).
     Max 3deg, transform-only, rAF-throttled, settles softly on leave. Delegated
     listeners so AJAX-loaded cards (filtering, pagination) work without re-init. */
  function initTilt() {
    if (initTilt._bound || REDUCE || !FINE) return;
    initTilt._bound = true;
    var MAX = 3, raf = null, target = null, rx = 0, ry = 0;
    function apply() {
      raf = null;
      if (target) target.style.transform = 'perspective(900px) rotateX(' + rx + 'deg) rotateY(' + ry + 'deg)';
    }
    document.addEventListener('pointermove', function (e) {
      var card = e.target.closest && e.target.closest('product-item.product-collection, a.cp-card-link, a.rp-pc');
      if (!card) return;
      var r = card.getBoundingClientRect();
      if (r.width < 40) return;
      target = card;
      card.classList.add('is-tilting');
      ry = ((e.clientX - r.left) / r.width - 0.5) * (MAX * 2);
      rx = -((e.clientY - r.top) / r.height - 0.5) * (MAX * 2);
      if (!raf) raf = requestAnimationFrame(apply);
    }, { passive: true });
    document.addEventListener('pointerout', function (e) {
      var card = e.target.closest && e.target.closest('product-item.product-collection, a.cp-card-link, a.rp-pc');
      if (!card) return;
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;
      card.classList.remove('is-tilting');
      card.style.transform = '';
      if (target === card) target = null;
    }, { passive: true });
  }

  /* ---------------- editorial collage ----------------
     Layout is pure CSS columns now (the measured-masonry version collapsed
     while lazy images had no height). JS only clears stale inline spans left
     by the old engine so nothing fights the column flow. */
  function initCollage(root) {
    (root || document).querySelectorAll('[data-rp-collage]:not([data-rp-ready])').forEach(function (grid) {
      grid.setAttribute('data-rp-ready', '1');
      grid.querySelectorAll('.rp-cg__cell').forEach(function (cell) { cell.style.gridRowEnd = ''; });
    });
  }

  /* ---------------- frame match: drag the seam ---------------- */
  function initCompare(root) {
    (root || document).querySelectorAll('[data-rp-compare]:not([data-rp-ready])').forEach(function (stage) {
      stage.setAttribute('data-rp-ready', '1');
      var over = stage.querySelector('.rp-cmp__over');
      var handle = stage.querySelector('.rp-cmp__handle');
      if (!over || !handle) return;
      var dragging = false;
      function setP(clientX) {
        var r = stage.getBoundingClientRect();
        var p = Math.min(0.94, Math.max(0.06, (clientX - r.left) / r.width));
        over.style.clipPath = 'inset(0 ' + ((1 - p) * 100).toFixed(2) + '% 0 0)';
        handle.style.left = (p * 100).toFixed(2) + '%';
      }
      stage.addEventListener('pointerdown', function (e) {
        dragging = true;
        stage.setPointerCapture(e.pointerId);
        setP(e.clientX);
        e.preventDefault();
      });
      stage.addEventListener('pointermove', function (e) { if (dragging) setP(e.clientX); });
      function up() { dragging = false; }
      stage.addEventListener('pointerup', up);
      stage.addEventListener('pointercancel', up);
      handle.addEventListener('keydown', function (e) {
        var cur = parseFloat(handle.style.left) || 50;
        if (e.key === 'ArrowLeft') { setP(stage.getBoundingClientRect().left + stage.offsetWidth * (cur - 4) / 100); e.preventDefault(); }
        if (e.key === 'ArrowRight') { setP(stage.getBoundingClientRect().left + stage.offsetWidth * (cur + 4) / 100); e.preventDefault(); }
      });
    });
  }

  /* ---------------- world switcher tabs ---------------- */
  function initWtabs(root) {
    (root || document).querySelectorAll('[data-rp-wtabs]:not([data-rp-ready])').forEach(function (sec) {
      sec.setAttribute('data-rp-ready', '1');
      sec.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-rp-wt-tab]');
        if (!tab) return;
        var ix = tab.getAttribute('data-rp-wt-tab');
        sec.querySelectorAll('[data-rp-wt-tab]').forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-act', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        sec.querySelectorAll('[data-rp-wt-bg]').forEach(function (b) {
          b.classList.toggle('is-act', b.getAttribute('data-rp-wt-bg') === ix);
        });
        sec.querySelectorAll('[data-rp-wt-panel]').forEach(function (p) {
          p.classList.toggle('is-act', p.getAttribute('data-rp-wt-panel') === ix);
        });
      });
    });
  }

  /* ---------------- drop countdown ---------------- */
  function initCountdown(root) {
    (root || document).querySelectorAll('[data-rp-countdown]:not([data-rp-ready])').forEach(function (box) {
      box.setAttribute('data-rp-ready', '1');
      var raw = (box.getAttribute('data-rp-cd-to') || '').trim();
      var target = raw ? new Date(raw.replace(' ', 'T')) : null;
      if (!target || isNaN(target.getTime())) return;
      var dEl = box.querySelector('[data-rp-cd-d]'), hEl = box.querySelector('[data-rp-cd-h]');
      var mEl = box.querySelector('[data-rp-cd-m]'), sEl = box.querySelector('[data-rp-cd-s]');
      var head = box.querySelector('[data-rp-cd-head]');
      function two(n) { return ('0' + n).slice(-2); }
      function tick() {
        var diff = target.getTime() - Date.now();
        if (diff <= 0) {
          clearInterval(iv);
          box.classList.add('is-live');
          if (head) {
            var live = head.getAttribute('data-rp-cd-live');
            if (live) head.textContent = live;
          }
          var clock = box.querySelector('[data-rp-cd-clock]');
          if (clock) clock.style.display = 'none';
          return;
        }
        var sec = Math.floor(diff / 1000);
        if (dEl) dEl.textContent = two(Math.floor(sec / 86400));
        if (hEl) hEl.textContent = two(Math.floor(sec % 86400 / 3600));
        if (mEl) mEl.textContent = two(Math.floor(sec % 3600 / 60));
        if (sEl) sEl.textContent = two(sec % 60);
      }
      var iv = setInterval(tick, 1000);
      tick();
    });
  }

  /* ---------------- kinetic lines: scroll-linked drift, capped ---------------- */
  var kinEls = [];
  var kinRaf = null;
  function kinTick() {
    kinRaf = null;
    var vh = window.innerHeight;
    for (var i = 0; i < kinEls.length; i++) {
      var el = kinEls[i];
      var r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) continue;
      var dir = parseFloat(el.getAttribute('data-rp-kin')) || 1;
      var prog = (r.top + r.height / 2 - vh / 2) / vh;
      el.style.transform = 'translateX(' + (-prog * dir * 9).toFixed(2) + '%)';
    }
  }
  function initKinetic(root) {
    if (REDUCE) return;
    (root || document).querySelectorAll('[data-rp-kin]').forEach(function (el) {
      if (kinEls.indexOf(el) === -1) kinEls.push(el);
    });
    if (kinEls.length && !initKinetic._bound) {
      initKinetic._bound = true;
      window.addEventListener('scroll', function () { if (!kinRaf) kinRaf = requestAnimationFrame(kinTick); }, { passive: true });
      kinTick();
    }
  }

  /* ---------------- video band: play in view, pause off ---------------- */
  function initVideoBand(root) {
    (root || document).querySelectorAll('[data-rp-videoband]:not([data-rp-ready])').forEach(function (box) {
      box.setAttribute('data-rp-ready', '1');
      var vid = box.querySelector('video');
      if (!vid) return;
      if (REDUCE) { vid.removeAttribute('autoplay'); return; }
      if (!('IntersectionObserver' in window)) { vid.play().catch(function () {}); return; }
      var io = new IntersectionObserver(function (en) {
        en.forEach(function (e) {
          if (e.isIntersecting) vid.play().catch(function () {});
          else vid.pause();
        });
      }, { threshold: 0.15 });
      io.observe(box);
    });
  }

  /* ---------------- horizontal journey: pinned track, scroll-linked X ----------------
     The section gets tall (track overflow becomes vertical scroll length); the
     viewport pins via CSS sticky; progress maps to translateX. Touch + reduced
     motion fall back to a native horizontal strip (CSS handles it). */
  var jrSections = [];
  var jrRaf = null;
  function jrTick() {
    jrRaf = null;
    var vh = window.innerHeight;
    for (var i = 0; i < jrSections.length; i++) {
      var sec = jrSections[i];
      var track = sec._rpTrack;
      var r = sec.getBoundingClientRect();
      var span = r.height - vh;
      if (span <= 0) continue;
      var p = Math.min(1, Math.max(0, -r.top / span));
      var max = track.scrollWidth - track.parentElement.clientWidth + 48;
      if (max > 0) track.style.transform = 'translateX(' + (-p * max).toFixed(1) + 'px)';
    }
  }
  function initJourney(root) {
    (root || document).querySelectorAll('[data-rp-journey]:not([data-rp-ready])').forEach(function (sec) {
      sec.setAttribute('data-rp-ready', '1');
      var track = sec.querySelector('[data-rp-jr-track]');
      if (!track) return;
      if (REDUCE || !FINE) { sec.classList.add('rp-jr--strip'); return; }
      sec._rpTrack = track;
      function size() {
        var extra = track.scrollWidth - track.parentElement.clientWidth;
        if (extra <= 0) { sec.classList.add('rp-jr--strip'); sec.style.height = ''; return; }
        sec.classList.remove('rp-jr--strip');
        sec.style.height = 'calc(100vh + ' + Math.round(extra * 1.15) + 'px)';
      }
      size();
      window.addEventListener('resize', size);
      window.addEventListener('load', size);
      if (jrSections.indexOf(sec) === -1) jrSections.push(sec);
      if (!initJourney._bound) {
        initJourney._bound = true;
        window.addEventListener('scroll', function () { if (!jrRaf) jrRaf = requestAnimationFrame(jrTick); }, { passive: true });
        jrTick();
      }
    });
  }

  /* ---------------- boot + theme-editor lifecycle ----------------
     R17 perf (TBT): split boot. CRITICAL inits affect content visibility or
     respond to a click/tap, so they run immediately. DECORATIVE inits (scroll
     parallax, marquee duplication, tilt, kinetic, etc.) are pure enhancement —
     they run in idle time so they don't block the main thread during the
     0-2s window Lighthouse scores for Total Blocking Time. In the theme editor
     (shopify:section:load) everything runs synchronously so edits feel instant. */
  function bootCritical(root) {
    initStagger(root); initReveals(root); initGallery(root);
    initFaq(root); initAccordion(); initVideo(root); initCompare(root);
    initWtabs(root); initMarqueeScrub(root); initCountdown(root);
    initFaqPage(root); initLegal(root);
  }
  function bootDecorative(root) {
    initParallax(root); initManifesto(root); initStats(root);
    initMarquee(root); initTilt(); initHotspot();
    initCollage(root); initKinetic(root); initVideoBand(root); initJourney(root);
  }
  function boot(root, idle) {
    bootCritical(root);
    if (idle && 'requestIdleCallback' in window) requestIdleCallback(function () { bootDecorative(root); }, { timeout: 1800 });
    else if (idle) setTimeout(function () { bootDecorative(root); }, 1);
    else bootDecorative(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document, true); });
  else boot(document, true);

  document.addEventListener('shopify:section:load', function (e) { boot(e.target, false); });
  document.addEventListener('shopify:section:unload', function () {
    if (window.ScrollTrigger) { try { window.ScrollTrigger.refresh(); } catch (x) {} }
  });

  /* ScrollTrigger positions shift when the Sequel fonts swap in */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      if (window.ScrollTrigger) { try { window.ScrollTrigger.refresh(); } catch (x) {} }
    });
  }
})();
